import { useQuery } from '@apollo/client';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    dangerItemClassName,
    dangerMenuIconClassName,
    DeleteConfirm,
    DeleteMenuIcon,
    EditMenuIcon,
    itemClassName,
    menuIconClassName,
    OpenChatMenuIcon,
    PauseMenuIcon,
    ResumeMenuIcon,
    RunNowMenuIcon,
} from './AutomationCard.tsx';
import {
    formatCadenceLong,
    formatCadenceWithZone,
    formatDay,
    formatNextRun,
    fromAutomationState,
    RUNS_PAGE_SIZE,
    formatRecipients,
    runErrorMessage,
    zoneName,
} from './scheduleFormat.ts';
import ScheduleRuns, { type RunStatusFilter } from './ScheduleRuns.tsx';
import { useScheduleActions } from './useScheduleActions.ts';
import Alert from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import Page from '../../../core/components/Page';
import { useToaster } from '../../../core/components/Toast.tsx';
import { skipPollAttemptWhenHidden } from '../../../core/utils/hooks/useRefetchOnTabVisible.ts';
import { url } from '../../../core/utils/link.ts';
import { AUTOMATIONS_PATH, CONVERSATION_PATH, SCHEDULE_RUNS_UPDATE_INTERVAL } from '../../constants.ts';
import { graphqlErrorMessage } from '../../errors.ts';
import { GET_CONVERSATION_SCHEDULE } from '../../queries.ts';
import { SidebarToggle } from '../SidebarToggle.tsx';

const EllipsisIcon = withIcon(faEllipsisVertical);

/** The status box: auto-paused, ended, paused or active (cadence, next run and where results go). */
const ScheduleStatus = ({ schedule, onResume, busy }: { schedule: any; onResume: () => void; busy: boolean }) => {
    const { t, i18n } = useTranslation('chat');
    const resumeButton = (
        <Button variant="outline" className="flex-none" disabled={busy} onClick={onResume}>
            {t('automations.menu.resume')}
        </Button>
    );
    const box = (severity: string, title: string, text: string | null, action: React.ReactNode = null) => (
        <Alert severity={severity}>
            <div className="flex w-full items-start justify-between gap-4">
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-medium">{title}</span>
                    {text && <span>{text}</span>}
                </div>
                {action}
            </div>
        </Alert>
    );

    if (schedule.autoPaused)
        return box(
            'error',
            t('automations.autoPaused'),
            schedule.lastRunError
                ? t('automations.detail.autoPausedText', { error: runErrorMessage(t, schedule.lastRunError) })
                : null,
            resumeButton
        );
    if (schedule.isEnded)
        return box(
            'info',
            t('automations.detail.endedTitle', {
                date: schedule.endsOn ? formatDay(schedule.endsOn, i18n.language) : '',
            }),
            null
        );
    if (!schedule.active)
        return box(
            'info',
            t('automations.detail.pausedTitle'),
            t('automations.detail.pausedText', {
                cadence: formatCadenceLong(t, schedule),
                timezone: t('automations.detail.timezoneLabel', { zone: zoneName(schedule.timezone || 'UTC') }),
            }),
            resumeButton
        );

    const file =
        schedule.exportFormat && schedule.exportFormat !== 'message'
            ? ` (${t(`automations.formats.${schedule.exportFormat}`)})`
            : '';
    const toChat = schedule.deliverToConversation !== false;
    const people = toChat ? formatRecipients(t, schedule) : '';
    const where = !schedule.emailRecipients?.length
        ? t(people ? 'automations.detail.resultChatPeople' : 'automations.detail.resultChat', { people })
        : toChat
          ? t(people ? 'automations.detail.resultChatPeopleEmail' : 'automations.detail.resultChatEmail', {
                people,
                file,
            })
          : t('automations.detail.resultEmail', { file });
    return box(
        'info',
        formatCadenceWithZone(t, schedule),
        schedule.nextRunAt
            ? t('automations.detail.activeText', {
                  next: formatNextRun(schedule.nextRunAt, i18n.language, schedule.timezone),
                  where,
              })
            : where
    );
};

interface ScheduleDetailProps {
    scheduleId: string;
    onEdit: (schedule: any) => void;
    onOpenSidebar: () => void;
}

export const ScheduleDetail = ({ scheduleId, onEdit, onOpenSidebar }: ScheduleDetailProps) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const { pause, resume, runNow, remove, busy } = useScheduleActions();
    const [deleting, setDeleting] = useState(false);
    const [runsStatus, setRunsStatus] = useState<RunStatusFilter>(null);
    const [runsPage, setRunsPage] = useState(1);
    const [runsPageSize, setRunsPageSize] = useState(RUNS_PAGE_SIZE);
    useEffect(() => {
        setRunsStatus(null);
        setRunsPage(1);
    }, [scheduleId]);

    const errorShownRef = useRef(false);
    const { data, previousData, loading, refetch } = useQuery(GET_CONVERSATION_SCHEDULE, {
        variables: {
            id: scheduleId,
            runsLimit: runsPageSize,
            runsOffset: (runsPage - 1) * runsPageSize,
            runsStatus,
        },
        pollInterval: SCHEDULE_RUNS_UPDATE_INTERVAL,
        skipPollAttempt: skipPollAttemptWhenHidden,
        onError: (error) => {
            if (errorShownRef.current) return;
            errorShownRef.current = true;
            publishToast({ description: graphqlErrorMessage(error, t('automations.toasts.error')), status: 'error' });
        },
    });
    const previousSchedule = previousData?.agentConversationSchedule;
    const schedule =
        data?.agentConversationSchedule ?? (previousSchedule?.id === scheduleId ? previousSchedule : undefined);
    const runsLoading = loading && !data?.agentConversationSchedule;
    const openChat = () =>
        navigate(url(CONVERSATION_PATH, { conversationId: schedule.conversationId }), {
            state: fromAutomationState(schedule),
        });
    const thenRefetch = (action: Promise<boolean>) =>
        action.then((ok) => (ok ? refetch().catch(() => undefined) : undefined));

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <Page.Header className="flex items-center">
                <Page.HeaderBreadcrumb to={url(AUTOMATIONS_PATH)}>{t('automations.detail.back')}</Page.HeaderBreadcrumb>
                <Page.HeaderTitle className="truncate">{schedule?.name}</Page.HeaderTitle>
                <div className="ml-auto flex items-center gap-2">
                    {schedule && (
                        <>
                            <Page.HeaderButton type="button" variant="outline" onClick={() => onEdit(schedule)}>
                                <EditMenuIcon />
                                {t('automations.menu.edit')}
                            </Page.HeaderButton>
                            <DropdownMenu>
                                <DropdownMenu.Trigger asChild>
                                    <Page.HeaderIconButton
                                        type="button"
                                        variant="ghost"
                                        aria-label={t('automations.menu.more')}
                                        disabled={busy}
                                    >
                                        <EllipsisIcon />
                                    </Page.HeaderIconButton>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align="end">
                                    <DropdownMenu.Item className={itemClassName} onClick={openChat}>
                                        <OpenChatMenuIcon className={menuIconClassName} />
                                        {t('automations.menu.openChat')}
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className={itemClassName}
                                        onClick={() => thenRefetch(runNow(schedule))}
                                    >
                                        <RunNowMenuIcon className={menuIconClassName} />
                                        {t('automations.menu.runNow')}
                                    </DropdownMenu.Item>
                                    {!schedule.isEnded && (
                                        <>
                                            <DropdownMenu.Item
                                                className={itemClassName}
                                                onClick={() =>
                                                    thenRefetch(schedule.active ? pause(schedule) : resume(schedule))
                                                }
                                            >
                                                {schedule.active ? (
                                                    <PauseMenuIcon className={menuIconClassName} />
                                                ) : (
                                                    <ResumeMenuIcon className={menuIconClassName} />
                                                )}
                                                {schedule.active
                                                    ? t('automations.menu.pause')
                                                    : t('automations.menu.resume')}
                                            </DropdownMenu.Item>
                                        </>
                                    )}
                                    <DropdownMenu.Separator />
                                    <DropdownMenu.Item
                                        className={dangerItemClassName}
                                        onClick={() => setDeleting(true)}
                                    >
                                        <DeleteMenuIcon className={dangerMenuIconClassName} />
                                        {t('automations.menu.delete')}
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu>
                        </>
                    )}
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </Page.Header>
            <Page.Content lowered className="justify-center flex">
                {!schedule ? (
                    loading ? (
                        <Loader />
                    ) : (
                        <p className="text-sm text-content-medium">{t('automations.detail.notFound')}</p>
                    )
                ) : (
                    <div className="flex w-full max-w-5xl flex-col gap-6">
                        <ScheduleStatus
                            schedule={schedule}
                            busy={busy}
                            onResume={() => thenRefetch(resume(schedule))}
                        />
                        <ScheduleRuns
                            schedule={schedule}
                            isLoading={runsLoading}
                            status={runsStatus}
                            onStatusChange={(status) => {
                                setRunsStatus(status);
                                setRunsPage(1);
                            }}
                            page={runsPage}
                            pageSize={runsPageSize}
                            onPageChange={setRunsPage}
                            onPageSizeChange={(pageSize) => {
                                setRunsPageSize(pageSize);
                                setRunsPage(1);
                            }}
                        />
                    </div>
                )}
            </Page.Content>
            {deleting && schedule && (
                <DeleteConfirm
                    name={schedule.name}
                    kind="schedule"
                    disabled={busy}
                    onConfirm={async () => {
                        const ok = await remove(schedule);
                        setDeleting(false);
                        if (ok) navigate(url(AUTOMATIONS_PATH));
                    }}
                    onCancel={() => setDeleting(false)}
                />
            )}
        </div>
    );
};
